const EARTH_RADIUS_METRES = 6371008.8;

export function toRadians(value) {
  return value * Math.PI / 180;
}

export function haversineMetres(a, b) {
  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);
  const deltaLat = lat2 - lat1;
  const deltaLon = toRadians(b.longitude - a.longitude);
  const h = Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;
  return 2 * EARTH_RADIUS_METRES * Math.asin(Math.sqrt(h));
}

export function bearingDegrees(a, b) {
  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);
  const deltaLon = toRadians(b.longitude - a.longitude);
  const y = Math.sin(deltaLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLon);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

export function normalizeAngle(value) {
  return ((value % 360) + 360) % 360;
}

export function smoothAngle(previous, next, factor = 0.18) {
  if (!Number.isFinite(previous)) return normalizeAngle(next);
  const delta = ((next - previous + 540) % 360) - 180;
  return normalizeAngle(previous + delta * factor);
}

export function formatDistance(metres) {
  if (!Number.isFinite(metres)) return '—';
  if (metres < 1000) return `${Math.max(0, Math.round(metres))} m`;
  return `${(metres / 1000).toFixed(metres < 10000 ? 1 : 0)} km`;
}

export function finalApproachThreshold(accuracy) {
  const safeAccuracy = Number.isFinite(accuracy) ? accuracy : 20;
  return Math.min(65, Math.max(18, safeAccuracy * 1.25));
}

export function arrivalRadius(accuracy) {
  return Math.min(70, Math.max(12, Number.isFinite(accuracy) ? accuracy : 20));
}

export function circlePolygon(center, radiusMetres, points = 48) {
  const coordinates = [];
  const latitudeScale = 1 / 111320;
  const longitudeScale = 1 / (111320 * Math.cos(toRadians(center.latitude)));
  for (let index = 0; index <= points; index += 1) {
    const angle = index / points * Math.PI * 2;
    coordinates.push([
      center.longitude + Math.cos(angle) * radiusMetres * longitudeScale,
      center.latitude + Math.sin(angle) * radiusMetres * latitudeScale,
    ]);
  }
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coordinates] } };
}

export function distanceToRouteMetres(location, geometry) {
  if (!Array.isArray(geometry) || geometry.length === 0) return Infinity;
  const latitudeScale = 111320;
  const longitudeScale = latitudeScale * Math.cos(toRadians(location.latitude));
  let minimum = Infinity;
  for (let index = 0; index < geometry.length - 1; index += 1) {
    const ax = (geometry[index][0] - location.longitude) * longitudeScale;
    const ay = (geometry[index][1] - location.latitude) * latitudeScale;
    const bx = (geometry[index + 1][0] - location.longitude) * longitudeScale;
    const by = (geometry[index + 1][1] - location.latitude) * latitudeScale;
    const dx = bx - ax;
    const dy = by - ay;
    const denominator = dx * dx + dy * dy;
    const t = denominator === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / denominator));
    minimum = Math.min(minimum, Math.hypot(ax + t * dx, ay + t * dy));
  }
  return minimum;
}

export function closestRouteIndex(location, geometry) {
  let bestIndex = 0;
  let bestDistance = Infinity;
  geometry.forEach((coordinate, index) => {
    const distance = haversineMetres(location, { longitude: coordinate[0], latitude: coordinate[1] });
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });
  return bestIndex;
}

export function activeRouteStep(steps, routeIndex) {
  if (!Array.isArray(steps) || steps.length === 0) return null;
  return steps.find((step) => Array.isArray(step.wayPoints) && routeIndex <= step.wayPoints[1]) || steps.at(-1);
}

export function buildFallbackRoute(origin, destination) {
  const midpoint = [
    (origin[0] + destination[0]) / 2 + (destination[1] - origin[1]) * 0.025,
    (origin[1] + destination[1]) / 2 - (destination[0] - origin[0]) * 0.025,
  ];
  const geometry = [origin, midpoint, destination];
  const distance = haversineMetres(
    { longitude: origin[0], latitude: origin[1] },
    { longitude: destination[0], latitude: destination[1] },
  );
  return {
    geometry,
    distance,
    duration: distance / 1.3,
    fallback: true,
    steps: [
      { instruction: 'Head toward the cache', type: 11, distance: distance * 0.45, wayPoints: [0, 1] },
      { instruction: 'Continue on foot', type: 6, distance: distance * 0.45, wayPoints: [1, 2] },
      { instruction: 'Final approach', type: 10, distance: distance * 0.1, wayPoints: [2, 2] },
    ],
  };
}
